import { initializeApp, FirebaseOptions } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  Messaging,
  isSupported,
  MessagePayload
} from 'firebase/messaging';
import { getInstallations, getId, Installations } from 'firebase/installations';
import { ToasterService } from './toaster';

declare const STAGE: string;
declare const customscripts: { nonce: string };

// Types pour les données de notification
interface NotificationData {
  post_type?: 'document' | 'post' | 'evenement';
  post_id?: string;
  document_id?: string;
  url?: string;
  op?: 'healthcheck';
  ts?: string;
  silent?: string;
}

interface FirebaseNotificationPayload {
  notification?: {
    title?: string;
    body?: string;
  };
  data?: NotificationData;
}

/**
 * Service qui récupère le Firebase Installation ID (FID) + le token FCM,
 * les envoie à l’API WordPress et tient compte des rotations de jeton.
 */
export class FirebaseMessagingService {
  private messaging!: Messaging;
  private installations!: Installations;
  private vapidKey: string;
  private config: FirebaseOptions;
  private toasterService: ToasterService;

  constructor() {
    // Initialiser le service toaster
    this.toasterService = new ToasterService();

    // ---------- CONFIGS ----------
    const stagingConfig: FirebaseOptions = {
      apiKey: 'AIzaSyDLFk-Zx8iMOrFDjPXqMEFYXbhHtnHNlRQ',
      authDomain: 'cotelub-intranet-dev.firebaseapp.com',
      projectId: 'cotelub-intranet-dev',
      storageBucket: 'cotelub-intranet-dev.firebasestorage.app',
      messagingSenderId: '1085006317542',
      appId: '1:1085006317542:web:7e508b1904ea3ad12eb827'
    };

    this.config = stagingConfig;
    this.vapidKey =
      'BPeNJJSoULb-e0RtPl-7T3fI1hDOq-nRtV3tEyzK8TwvLOJsVubeBdseFzgUtmYvKkiJT7DwFztqyx6WwGI3z_A';

    if (STAGE === 'production') {
      const prodConfig: FirebaseOptions = {
        apiKey: 'AIzaSyDLFk-Zx8iMOrFDjPXqMEFYXbhHtnHNlRQ',
        authDomain: 'cotelub-intranet-dev.firebaseapp.com',
        projectId: 'cotelub-intranet-dev',
        storageBucket: 'cotelub-intranet-dev.firebasestorage.app',
        messagingSenderId: '1085006317542',
        appId: '1:1085006317542:web:7e508b1904ea3ad12eb827'
      };
      this.config = prodConfig;
      this.vapidKey =
        'BPeNJJSoULb-e0RtPl-7T3fI1hDOq-nRtV3tEyzK8TwvLOJsVubeBdseFzgUtmYvKkiJT7DwFztqyx6WwGI3z_A';
    }

    const loginPage = document.querySelector('.page-template-tpl-login');
    if (!loginPage) {
      this.askPermission();
      this.init();
    }
  }

  public async init(): Promise<void> {
    await this._initFirebase();
  }

  private async _initFirebase(): Promise<void> {
    if (!(await isSupported())) return;

    try {
      const app = initializeApp(this.config);
      this.messaging = getMessaging(app);
      this.installations = getInstallations(app);

      const fid: string = await getId(this.installations);
      const initialToken: string | null = await this._getCurrentToken();

      if (navigator.onLine) {
        await this._sendTokenToServer(fid, initialToken);
      }

      onMessage(this.messaging, (payload: FirebaseNotificationPayload) => {
        this.showNotificationToast(payload);
      });
    } catch (error) {
      console.error('Firebase init error:', error);
    }
  }

  private async askPermission(): Promise<void> {
    const fcmButton: Element | null = document.querySelector('[fcmButton]');

    fcmButton?.addEventListener('click', async () => {
      const permission: NotificationPermission =
        await Notification.requestPermission();

      await this._initFirebase();

      if (permission !== 'granted') return;
    });
  }

  private async _getCurrentToken(): Promise<string | null> {
    try {
      return await getToken(this.messaging, { vapidKey: this.vapidKey });
    } catch (err) {
      console.error('Erreur lors de getToken FCM:', err);
      return null;
    }
  }

  private async _sendTokenToServer(
    fid: string,
    token: string | null
  ): Promise<void> {
    if (!token) return;
    try {
      await fetch('/wp-json/wp/v2/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': (window as any).customscripts.ajax_nonce
        },
        body: JSON.stringify({ fid, token })
      });
    } catch (e) {
      console.error("Échec d'envoi du token au serveur:", e);
    }
  }

  /**
   * Affiche une notification Firebase dans un toaster en haut à droite
   */
  private showNotificationToast(payload: FirebaseNotificationPayload): void {
    // Ignorer les messages de healthcheck
    if (payload.data?.op === 'healthcheck') {
      return;
    }

    // Extraire les données de la notification
    const notification = payload.notification;
    const data = payload.data;

    if (!notification) return;

    // Construire le message - utiliser les valeurs originales de Firebase
    let message: string = notification.body || 'Nouvelle notification';
    let title: string = notification.title || 'Notification';
    let icon: string = '🔔';

    // Si c'est un document, garder le titre original (nom du fichier) et adapter le message
    if (data?.post_type === 'document') {
      // Garder le titre original (nom du fichier) sans icône (évite la répétition)
      title = notification.title || 'Nouveau document';
      message = `Un nouveau document a été ajouté à l'intranet`;
      icon = '📄';
    } else if (data?.post_type === 'evenement') {
      title = notification.title || 'Nouvel événement';
      message =
        notification.body || "Un nouvel événement a été ajouté à l'agenda";
      icon = '📅';
    } else if (data?.post_type === 'post') {
      title = notification.title || 'Nouvelle actualité';
      message = notification.body || 'Une nouvelle actualité est disponible';
      icon = '📰';
    }

    // Utiliser le ToasterService pour afficher la notification
    this.toasterService.showFirebaseNotification(
      title,
      message,
      icon,
      data,
      8000
    );
  }
}

export const firebaseMessagingService = new FirebaseMessagingService();
